import type { Order } from '@/utils/types';

export interface SeatSelection {
  selectedSeats: string[];
  selectedSeatsIds: string[];
}

export type SeatSelectionIssueReason = 'DUPLICATED_SEAT' | 'SEAT_COUNT_MISMATCH';

export interface SeatSelectionIssue {
  experienceId: string;
  startDate: number;
  reason: SeatSelectionIssueReason;
}

interface SeatMapEntry {
  id?: string;
  status?: string;
  startDate?: number;
}

type SeatMap = Record<string, Record<string, SeatMapEntry>>;

const hasDuplicates = (values: string[]) => new Set(values).size !== values.length;

export const addSeatToSelection = (selection: Partial<SeatSelection>, seat: string, seatId: string): SeatSelection => {
  const selectedSeats = selection.selectedSeats ?? [];
  const selectedSeatsIds = selection.selectedSeatsIds ?? [];

  // O código `fila-coluna` se repete entre os setores do mapa; só o id identifica o assento.
  if (selectedSeatsIds.includes(seatId)) {
    return { selectedSeats: [...selectedSeats], selectedSeatsIds: [...selectedSeatsIds] };
  }

  return { selectedSeats: [...selectedSeats, seat], selectedSeatsIds: [...selectedSeatsIds, seatId] };
};

// selectedSeats e selectedSeatsIds são paralelos por índice: remover pelo id tem que levar o código junto.
export const removeSeatIdsFromSelection = (
  selection: Partial<SeatSelection>,
  seatIdsToRemove: ReadonlySet<string>
): SeatSelection => {
  const selectedSeats = selection.selectedSeats ?? [];
  const selectedSeatsIds = selection.selectedSeatsIds ?? [];

  return {
    selectedSeats: selectedSeats.filter((_, index) => {
      const seatId = selectedSeatsIds[index];
      return seatId === undefined || !seatIdsToRemove.has(seatId);
    }),
    selectedSeatsIds: selectedSeatsIds.filter((seatId) => !seatIdsToRemove.has(seatId)),
  };
};

export const collectSoldSeatIds = (seatMaps: SeatMap[], startDate: number): Set<string> => {
  const soldSeatIds = new Set<string>();

  seatMaps.forEach((seatMap) => {
    Object.values(seatMap).forEach((row) => {
      Object.values(row).forEach((seat) => {
        if (seat?.id && seat.startDate === startDate && seat.status === 'OCCUPIED') soldSeatIds.add(seat.id);
      });
    });
  });

  return soldSeatIds;
};

export const clearSeatSelection = <T extends Order>(orders: T[]): T[] =>
  orders.map((order) => ({ ...order, selectedSeats: [], selectedSeatsIds: [] }));

// Pedido sem assento é experiência sem lugar marcado; com assento, cada ingresso precisa de exatamente um lugar.
export const findSeatSelectionIssues = (orders: Order[]): SeatSelectionIssue[] => {
  return orders.flatMap((order) => {
    const selectedSeats = order.selectedSeats ?? [];
    const selectedSeatsIds = order.selectedSeatsIds ?? [];

    if (selectedSeats.length === 0 && selectedSeatsIds.length === 0) return [];

    const issue = (reason: SeatSelectionIssueReason): SeatSelectionIssue => ({
      experienceId: order.experience.id,
      startDate: order.startDate,
      reason,
    });

    if (hasDuplicates(selectedSeatsIds)) return [issue('DUPLICATED_SEAT')];

    const ticketsAmount = order.tickets.reduce((total, ticket) => total + ticket.amount, 0);
    if (selectedSeatsIds.length !== ticketsAmount || selectedSeats.length !== selectedSeatsIds.length) {
      return [issue('SEAT_COUNT_MISMATCH')];
    }

    return [];
  });
};
